import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Video, Clock, FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getPlenarySessions } from '../services/api';

const Plenary = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const sessionsData = await getPlenarySessions();
        setSessions(sessionsData);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const upcomingSessions = sessions.filter(s => s.status === 'Upcoming');
  const completedSessions = sessions.filter(s => s.status === 'Completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Plenary Sessions</h1>
          <p className="text-xl text-white/90">Watch live sessions and access recordings of parliamentary proceedings</p>
        </div>
      </section>

      {/* Live Session Banner */}
      {upcomingSessions.length > 0 && (
        <section className="bg-gradient-to-r from-yellow-400 to-yellow-600 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                  <Video className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black">Next Session: {upcomingSessions[0].title}</h3>
                  <p className="text-black/80">{upcomingSessions[0].date}</p>
                </div>
              </div>
              <Button size="lg" className="bg-black text-white hover:bg-gray-900">
                <Video className="mr-2 h-5 w-5" />
                Join Live Stream
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="mb-8">
              <TabsTrigger value="upcoming">Upcoming Sessions ({upcomingSessions.length})</TabsTrigger>
              <TabsTrigger value="completed">Past Sessions ({completedSessions.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
              <div className="space-y-6">
                {upcomingSessions.map(session => (
                  <Card key={session.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-2xl font-bold mb-2">{session.title}</h3>
                          <div className="flex flex-wrap gap-4 text-white/90">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{session.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              <span>{session.location}</span>
                            </div>
                          </div>
                        </div>
                        <Badge className="bg-yellow-400 text-black hover:bg-yellow-500">{session.status}</Badge>
                      </div>
                      <Button className="bg-white text-red-600 hover:bg-gray-100">
                        <Video className="mr-2 h-4 w-4" />
                        Watch Live
                      </Button>
                    </div>
                    <CardContent className="p-6">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-yellow-600" />
                        Session Agenda
                      </h4>
                      <ul className="space-y-2">
                        {session.agenda.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700">
                            <span className="text-yellow-600 font-semibold">{index + 1}.</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="completed">
              <div className="space-y-6">
                {completedSessions.map(session => (
                  <Card key={session.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                    <CardHeader className="bg-gray-100">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-xl mb-2">{session.title}</CardTitle>
                          <CardDescription className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{session.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              <span>{session.location}</span>
                            </div>
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="border-gray-400">{session.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-yellow-600" />
                        Session Agenda
                      </h4>
                      <ul className="space-y-2 mb-6">
                        {session.agenda.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700 text-sm">
                            <span className="text-yellow-600 font-semibold">{index + 1}.</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex gap-3">
                        <Button variant="outline" className="flex-1 group-hover:bg-red-600 group-hover:text-white transition-colors">
                          <Video className="mr-2 h-4 w-4" />
                          Watch Recording
                        </Button>
                        <Button variant="outline" className="flex-1 group-hover:bg-black group-hover:text-white transition-colors">
                          <FileText className="mr-2 h-4 w-4" />
                          View Transcript
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Video Archive Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Video Archive</h2>
            <Button variant="outline" className="border-black text-black hover:bg-black hover:text-white">
              View All Videos
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <Card key={item} className="overflow-hidden hover:shadow-lg transition-shadow group">
                <div className="relative aspect-video bg-gray-900">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform cursor-pointer">
                      <Video className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs">
                    2:45:30
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-base group-hover:text-red-600 transition-colors">
                    Plenary Session Highlight {item}
                  </CardTitle>
                  <CardDescription>2025-0{7 - item}-15</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Plenary;